import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getEventTrails } from '@/services/api';
import {
  EventTrailEntry,
  EventTrailsResponse,
  EventTrailsParams,
} from '@/types/eventTrails';
import type { RootState } from '../index';

export type AuditTrailState =
  | 'initial'
  | 'loading'
  | 'ok'
  | 'error'
  | 'forbidden';

interface AuditTrailStateType {
  // Data
  data: EventTrailEntry[];
  pagination: EventTrailsResponse['pagination'] | null;
  state: AuditTrailState;
  error?: string;
  lastRequestKey?: string;
}

const initialState: AuditTrailStateType = {
  data: [],
  pagination: null,
  state: 'initial',
  error: undefined,
  lastRequestKey: undefined,
};

const getRequestKey = (params?: EventTrailsParams) =>
  JSON.stringify(params ?? {});

// Async thunk for fetching event trails
export const fetchEventTrails = createAsyncThunk(
  'auditTrail/fetchEventTrails',
  async (params: EventTrailsParams = {}, { rejectWithValue }) => {
    try {
      const response = await getEventTrails(params);
      return {
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch event trails';
      if (errorMessage.includes('Unauthorized')) {
        return rejectWithValue({
          state: 'forbidden' as AuditTrailState,
          error: 'Access denied to audit trail',
        });
      }
      return rejectWithValue({
        state: 'error' as AuditTrailState,
        error: errorMessage,
      });
    }
  },
  {
    condition: (params, { getState }) => {
      const state = getState() as RootState;
      if (state.auditTrail.state !== 'loading') {
        return true;
      }
      return state.auditTrail.lastRequestKey !== getRequestKey(params);
    },
  }
);

const auditTrailSlice = createSlice({
  name: 'auditTrail',
  initialState,
  reducers: {
    setData: (
      state,
      action: PayloadAction<{
        data: EventTrailEntry[];
        pagination?: EventTrailsResponse['pagination'];
      }>
    ) => {
      state.data = action.payload.data;
      if (action.payload.pagination) {
        state.pagination = action.payload.pagination;
      }
      state.state = 'ok';
      state.error = undefined;
    },
    setState: (state, action: PayloadAction<AuditTrailState>) => {
      state.state = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.state = 'error';
    },
    setInitialState: state => {
      state.state = 'initial';
      state.error = undefined;
    },
    setLoadingState: state => {
      state.state = 'loading';
      state.error = undefined;
    },
    setOkState: state => {
      state.state = 'ok';
      state.error = undefined;
    },
    setErrorState: (state, action: PayloadAction<string | undefined>) => {
      state.state = 'error';
      state.error = action.payload || 'Failed to load audit trail data';
    },
    setForbiddenState: state => {
      state.state = 'forbidden';
      state.error = 'Access denied to audit trail';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEventTrails.pending, (state, action) => {
        state.state = 'loading';
        state.error = undefined;
        state.lastRequestKey = getRequestKey(action.meta.arg);
      })
      .addCase(fetchEventTrails.fulfilled, (state, action) => {
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
        state.state = 'ok';
        state.error = undefined;
      })
      .addCase(fetchEventTrails.rejected, (state, action) => {
        const payload = action.payload as {
          state: AuditTrailState;
          error: string;
        };
        state.state = payload.state;
        state.error = payload.error;
      });
  },
});

export const {
  setData,
  setState,
  setError,
  setInitialState,
  setLoadingState,
  setOkState,
  setErrorState,
  setForbiddenState,
} = auditTrailSlice.actions;
export default auditTrailSlice.reducer;

// Selectors

export const selectAuditTrailData = (state: RootState) =>
  state.auditTrail.data;
export const selectAuditTrailPagination = (state: RootState) =>
  state.auditTrail.pagination;
export const selectAuditTrailState = (state: RootState) =>
  state.auditTrail.state;
export const selectAuditTrailError = (state: RootState) =>
  state.auditTrail.error;


